/**
 * The character naming this member on the scene: the one whose token they own here (linked to it — the link reaches
 * the DM and the token's owners alone — or, unlinked to this viewer, a token carrying that character's name), else
 * their first character; with none in the member list (it is read when the table opens, so a character placed since
 * is not in it yet), the name of a token they own that is linked to a character; null with neither.
 */
export function characterOf(member, objects, about) {
    const chars = member.characters ?? [];
    // A line about one token (its owners, its turn) names that token's character when it is theirs (issue 1494): a player
    // with two characters was named by the other one on the second's turn.
    const owned = Object.values(objects).filter((o) => o.owners?.includes(member.id));
    if (about?.owners?.includes(member.id))
        owned.unshift(about);
    for (const o of owned) {
        const linked = o.character ? chars.find((c) => c.id === o.character) : chars.find((c) => c.name === o.name);
        if (linked)
            return linked.name;
    }
    if (chars.length)
        return chars[0].name;
    return owned.find((o) => o.character && o.name?.trim())?.name?.trim() ?? null;
}
/**
 * The character a player's plain roll is credited to (issue 1553, Tom's choice A): the selected token's, when that token
 * is theirs and linked to a character; else the character of their token whose turn it is; else none (null), and the
 * log names them by their handle.
 */
export function creditedCharacter(actorId, objects, selected, turnTokens) {
    const theirs = (id) => {
        const o = objects[id];
        return o?.owners?.includes(actorId) && o.character ? o.character : null;
    };
    for (const id of [...(selected ? [selected] : []), ...turnTokens]) {
        const c = theirs(id);
        if (c)
            return c;
    }
    return null;
}
/**
 * A person's label: "You" for the viewer (or `opts.you`); "a former player" for anyone no longer at the table; the DM
 * as "@handle", their display name, or "the DM"; a player as "Character (@handle)" or "Character (Display name)",
 * just "@handle" / the display name with no character, the character alone with neither, and "a player" with nothing.
 * `about` is the token the line is about: its character is the one named, when it is theirs (issue 1494).
 */
export function memberLabel(member, objects = {}, opts = {}) {
    if (!member)
        return 'a former player';
    if (opts.meId && member.id === opts.meId)
        return opts.you ?? 'You';
    const base = member.handle ? `@${member.handle}` : member.displayName?.trim() || null;
    if (member.role === 'dm')
        return base ?? 'the DM';
    if (member.role === 'codm')
        return base ?? 'the co-DM';
    const character = characterOf(member, objects, opts.about);
    if (character && base)
        return `${character} (${base})`;
    return character ?? base ?? 'a player';
}
/** A role as the roster says it (issue 1040: a watcher is "watching", never "player"). */
export const roleWord = (role) => (role === 'dm' ? 'DM' : role === 'codm' ? 'co-DM' : role === 'watcher' ? 'watching' : 'player');
/**
 * A token's name as the turn order shows it (qa-runs 149): six "Goblin" tokens are "Goblin 1" to "Goblin 6" on the
 * strip, so a save row, a bonus prompt or an attack picker naming one says which. The combatant's name when the token is
 * that combatant's only one; else the token's own name, since a group's name ("Wolves") would name three tokens alike.
 * Reads the encounter the viewer was given, so a player is never told a name the strip does not show them.
 */
export function tokenLabel(o, encounter) {
    const c = encounter?.combatants.find((x) => x.tokens.includes(o.id));
    return c && c.tokens.length === 1 && c.name?.trim() ? c.name : o.name ?? null;
}
//# sourceMappingURL=names.js.map